import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppEnvironmentConfig } from 'projects/config/model/environment.config.model';
import { LangDefinition, TranslocoService } from '@ngneat/transloco';
import { getAccessTokenPayloadInfo } from 'projects/lib-shared-common/src/public-api';
import { BehaviorSubject, Observable, Subject, catchError, filter, finalize, map, of, shareReplay, switchMap, tap, throwError } from 'rxjs';
import { AvailableLanguages } from '../model/available-languages.model';
import { AuthService } from '../../../auth/auth.service';
import { SvcDataCacheService } from '../../../services/cache/svc-data-cache.service';
import { SvcCacheName } from '../../../services/cache/svc-cache-name.enum';
import { AnonymousTermsService, LanguageTerm } from './anonymous-terms.service';

export const AUTH_ROUTES_NEED_LOGGED = ['/sign-out', '/expired-password', '/multi-signin'];

interface CachedAuthenticatedTerms {
  userId: string | null;
  languageTag: string;
  terms: LanguageTerm[];
}

@Injectable({
  providedIn: 'root',
})
export class LanguagesService {
  private _authenticatedPendingRequest: Subject<AvailableLanguages[]>;
  private _authenticatedTerms$: Observable<LanguageTerm[]> | null = null;
  private _anonymousTermsServed = false;

  public _languages = new BehaviorSubject<AvailableLanguages[]>([]);
  public languages$ = this._languages.asObservable();

  private set _authenticatedLanguagesCached(value: AvailableLanguages[]) {
    this._svcDataCacheService.set(SvcCacheName.AUTHENTICATED_LANGUAGES, value);
  }
  private get _authenticatedLanguagesCached() {
    return this._svcDataCacheService.get<AvailableLanguages[]>(SvcCacheName.AUTHENTICATED_LANGUAGES);
  }

  private set _currentLanguageTagCached(value: any) {
    this._svcDataCacheService.set(SvcCacheName.LANGUAGE_TAG, value);
  }
  private get _currentLanguageTagCached() {
    return this._svcDataCacheService.get(SvcCacheName.LANGUAGE_TAG);
  }

  constructor(
    private _appConfig: AppEnvironmentConfig,
    private _authService: AuthService,
    private _httpClient: HttpClient,
    private _svcDataCacheService: SvcDataCacheService,
    private _anonymousTermsService: AnonymousTermsService
  ) {}

  public reset(): void {
    if (this._authenticatedPendingRequest) {
      this._authenticatedPendingRequest.complete();
      this._authenticatedPendingRequest = null;
    }
    this._authenticatedTerms$ = null;
    this._languages.next([]);
  }

  public getCachedAuthenticatedLanguages(): AvailableLanguages[] | null {
    const cached = this._authenticatedLanguagesCached;
    if (cached?.length) {
      return cached;
    }

    const inMemory = this._languages.value;
    return inMemory?.length ? inMemory : null;
  }

  public applyAvailableLangsTo(translocoService: TranslocoService, languages?: AvailableLanguages[]): void {
    const langs = languages ?? this.getCachedAuthenticatedLanguages();
    if (!langs?.length) {
      return;
    }

    translocoService.setAvailableLangs(
      langs.map((lang) => ({ id: lang.languageTag, label: lang.languageTranslated }))
    );
  }

  // AB#74965 - Transloco resolves a lang missing from availableLangs as a scope ('en-US' -> 'enUS.<key>'),
  // so the anonymous bootstrap registers the default lang before /Languages answers.
  public registerAvailableLang(translocoService: TranslocoService, lang: string): void {
    if (translocoService.isLang(lang)) {
      return;
    }

    translocoService.setAvailableLangs([...(translocoService.getAvailableLangs() as LangDefinition[]), { id: lang, label: lang }]);
  }

  // AB#73644 - Transloco keeps every loaded language in memory. It must be dropped when it may hold a dictionary that
  // is not the current user's authenticated one (login scope, anonymous routes, or terms invalidated by a language
  // change or a Multilingual edit); otherwise it is kept, so private navigation does not reload the dictionary.
  public clearStaleTranslations(translocoService: TranslocoService, force = false): void {
    if (!force && !this._anonymousTermsServed && this._getAuthenticatedTermsCached()) {
      return;
    }
    translocoService['cache'] = new Map();
    this._anonymousTermsServed = false;
  }

  public getAll(): Observable<AvailableLanguages[]> {
    const fromSignOut = AUTH_ROUTES_NEED_LOGGED.some((path) => location.pathname.includes(path)) || this._authService.getExpiredToken();
    return this._authService.check().pipe(
      switchMap((authenticated) => {
        if (authenticated && !fromSignOut) {
          if (this._authenticatedLanguagesCached) {
            return of(this._authenticatedLanguagesCached);
          } else if (!this._authenticatedPendingRequest) {
            this._authenticatedPendingRequest = new Subject();
            this._httpClient
              .get<AvailableLanguages[]>(`${this._appConfig.APIs.apiUrlAuth}/Languages/authenticated`)
              .pipe(
                tap((languages) => {
                  this._authenticatedLanguagesCached = languages;
                  this._authenticatedPendingRequest.next(languages);
                }),
                catchError((error) => {
                  this._authenticatedPendingRequest.error(error);
                  return throwError(() => error);
                }),
                finalize(() => {
                  this._authenticatedPendingRequest.complete();
                  this._authenticatedPendingRequest = null;
                })
              )
              .subscribe();
          }
          return this._authenticatedPendingRequest;
        } else {
          return this._httpClient.get<AvailableLanguages[]>(`${this._appConfig.APIs.apiUrlAuth}/Languages`);
        }
      }),
      tap((languages) => {
        this._languages.next(languages);
      })
    );
  }

  public getTerms(lang: string): Observable<LanguageTerm[]> {
    const fromSignOut = AUTH_ROUTES_NEED_LOGGED.some((path) => location.pathname.includes(path)) || this._authService.getExpiredToken();
    this.checkIfCurrentLanguageTag(lang);
    return this._authService.check().pipe(
      switchMap((authenticated) => {
        if (authenticated && !fromSignOut) {
          const cachedTerms = this._getAuthenticatedTermsCached(lang);
          if (cachedTerms) {
            return of(cachedTerms);
          }
          this._authenticatedTerms$ ??= this._fetchAuthenticatedTerms(lang);
          return this._authenticatedTerms$;
        } else {
          const langId = (this._languages?.value ?? this._authenticatedLanguagesCached)?.find((l) => l.languageTag === lang)?.languageId ?? 2;
          this._anonymousTermsServed = true;
          return this._anonymousTermsService.getTerms(langId);
        }
      })
    );
  }

  public updateUserLanguage(lang: string): Observable<boolean> {
    this._svcDataCacheService.remove(SvcCacheName.TERMS);
    const langId = this._authenticatedLanguagesCached?.find((l) => l.languageTag === lang)?.languageId ?? 2;
    return this._httpClient.put<boolean>(`${this._appConfig.APIs.apiUrlAuth}/UserPreference/change-language`, { languageId: langId }).pipe(
      map((response: any) => {
        if (response.isSucess) {
          this._svcDataCacheService.remove(SvcCacheName.TERMS);
        }
        return response.isSucess;
      })
    );
  }

  private _fetchAuthenticatedTerms(lang: string): Observable<LanguageTerm[]> {
    const userId = getAccessTokenPayloadInfo('UserId');
    const request$ = this._httpClient.get<LanguageTerm[]>(`${this._appConfig.APIs.apiUrlAuth}/Languages/terms/authenticated/v2`).pipe(
      tap((terms) => this._svcDataCacheService.set<CachedAuthenticatedTerms>(SvcCacheName.TERMS, { userId, languageTag: lang, terms })),
      finalize(() => {
        if (this._authenticatedTerms$ === request$) this._authenticatedTerms$ = null;
      }),
      shareReplay(1)
    );
    return request$;
  }

  // AB#73644 - TERMS survives signIn$, so it is reused only for the same user and language it was downloaded for.
  private _getAuthenticatedTermsCached(lang?: string): LanguageTerm[] | null {
    const cached = this._svcDataCacheService.get<CachedAuthenticatedTerms>(SvcCacheName.TERMS);
    const isOwner = cached?.userId === getAccessTokenPayloadInfo('UserId');
    const isLanguage = !lang || cached?.languageTag === lang;
    return Array.isArray(cached?.terms) && isOwner && isLanguage ? cached.terms : null;
  }

  private checkIfCurrentLanguageTag(currentLanguageTag: string): void {
    const fromSignOut = AUTH_ROUTES_NEED_LOGGED.some((path) => location.pathname.includes(path)) || this._authService.getExpiredToken();
    this._authService
      .check()
      .pipe(
        filter((authenticated) => authenticated && !fromSignOut),
        tap(() => {
          const cachedLanguageTag = this._currentLanguageTagCached;
          if (!cachedLanguageTag) {
            this._currentLanguageTagCached = currentLanguageTag;
          } else if (currentLanguageTag && cachedLanguageTag && currentLanguageTag !== cachedLanguageTag) {
            this._svcDataCacheService.remove(SvcCacheName.TERMS);
            this._currentLanguageTagCached = currentLanguageTag;
          }
        })
      )
      .subscribe();
  }
}
